import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { UiModule } from 'src/app/ui/ui.module';
import { ILabelOption } from 'src/app/features/settings/models/settings.models';
import * as smartInboxActions from '../../../store/smart-inbox.actions';
import { SettingsService } from 'src/app/features/settings/services/settings.services';

import { IConversationLabel } from '../../../models/conversation.models';

// ─────────────────────────────────────────────────────────────────────────────
// LabelPickerMenuComponent
// ─────────────────────────────────────────────────────────────────────────────
// "Pick a thing and attach it to this conversation", built on the same shape as
// customer-association-menu: a search box at the top, a scrollable list under
// it, one emitted event per action and no store writes of its own.
//
// It differs in two ways, both forced by the contract:
//   - the whole catalogue arrives in one request (the Labels screen's own 500
//     cap), so filtering is local and there is no scroll pagination;
//   - the search box doubles as the create box, because ASSIGN-02 is one action
//     rather than "create, then apply".
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-label-picker-menu',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, UiModule],
  templateUrl: './label-picker-menu.component.html',
  styleUrls: ['./label-picker-menu.component.scss'],
})
export class LabelPickerMenuComponent implements OnInit, AfterViewInit, OnDestroy {

  /** The thread's labels, as the API renders them: visible ones only. */
  @Input() appliedLabels: IConversationLabel[] = [];

  @Output() labelApplied = new EventEmitter<number>();
  @Output() labelRemoved = new EventEmitter<number>();
  /** A name to resolve-or-create, not a created id: the backend does both. */
  @Output() labelCreated = new EventEmitter<string>();

  searchControl = new FormControl('', { nonNullable: true });

  /**
   * Everything `GET /v1/labels` returned, hidden ones included. Kept whole
   * rather than filtered on arrival: `offerableLabels` hides them from the list,
   * while `hiddenNameCollision` needs them to explain why a name the user typed
   * cannot be created.
   */
  labels: ILabelOption[] = [];
  loading = false;
  /**
   * The catalogue could not be read. The route is gated on `settings_team`,
   * which an inbox operator need not have, so this is an ordinary state and not
   * an error case: the list is empty and creating by name — gated on
   * `smart_inbox` — still works.
   */
  loadFailed = false;

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  constructor(
    private settingsService: SettingsService,
    private actions$: Actions,
  ) {}

  ngOnInit(): void {
    this.loadLabels();

    // The panel stays open across an apply (ASSIGN-01), so a label invented here
    // would otherwise be missing from its own list until the menu was closed and
    // reopened -- the create succeeded, and the list that caused it did not move.
    this.actions$
      .pipe(ofType(smartInboxActions.UpdateThreadLabelsSuccess), takeUntil(this.destroy$))
      .subscribe(() => this.loadLabels());
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Reads ─────────────────────────────────────────────────────────────────

  get searchTerm(): string {
    return this.searchControl.value.trim();
  }

  /**
   * ASSIGN-03. A hidden label is not offered at all — not disabled, not greyed:
   * absent. Revoking `visible` is how an admin withdraws a label from
   * circulation, and a row that can still be clicked is the withdrawal not
   * happening.
   */
  get offerableLabels(): ILabelOption[] {
    const term = this.searchTerm.toLowerCase();
    return this.labels
      .filter((label) => label.visible)
      .filter((label) => !term || (label.name ?? '').toLowerCase().includes(term));
  }

  /**
   * ASSIGN-02's negative half. The backend resolves `create_and_apply` against
   * existing names case-insensitively, so typing "Doors" while "doors" exists
   * attaches the existing one. Offering "Create Doors" would promise a second
   * label the user will never get.
   */
  get canCreateTypedName(): boolean {
    return !!this.searchTerm && !this.nameMatch(this.searchTerm) && !this.loading;
  }

  /**
   * The typed name belongs to a label that exists but is hidden. Creating it is
   * refused for the same case-insensitive reason as above, and applying it would
   * come back invisible — so say so rather than showing a create button that
   * appears to do nothing.
   */
  get hiddenNameCollision(): boolean {
    const match = this.nameMatch(this.searchTerm);
    return !!this.searchTerm && !!match && !match.visible;
  }

  get isEmpty(): boolean {
    return !this.loading && this.offerableLabels.length === 0;
  }

  /**
   * Whether the list and its heading are worth rendering at all. When nothing
   * matched and the typed name can be created, the create row says everything
   * there is to say -- a "LABELS" heading over an empty box says it twice, and
   * leaves a band of dead space between the heading and the row.
   */
  get showList(): boolean {
    return this.loading || this.loadFailed || this.offerableLabels.length > 0
      || !this.canCreateTypedName;
  }

  isApplied(label: ILabelOption): boolean {
    return this.appliedLabels.some((applied) => applied.id === label.id);
  }

  trackByLabelId(_index: number, label: ILabelOption): number {
    return label.id;
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  toggleLabel(label: ILabelOption): void {
    if (this.isApplied(label)) {
      this.labelRemoved.emit(label.id);
    } else {
      this.labelApplied.emit(label.id);
    }
  }

  createTypedName(): void {
    if (!this.canCreateTypedName) return;

    this.labelCreated.emit(this.searchTerm);
    this.searchControl.setValue('');
  }

  // ── Private ───────────────────────────────────────────────────────────────

  /** Case-insensitive, like the backend's own resolution. */
  private nameMatch(name: string): ILabelOption | undefined {
    const wanted = name.toLowerCase();
    return this.labels.find((label) => (label.name ?? '').toLowerCase() === wanted);
  }

  private loadLabels(): void {
    this.loading = true;
    this.loadFailed = false;

    this.settingsService.listLabelOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.labels = response.data ?? [];
          this.loading = false;
        },
        // Cleared here as well as in `next`, rather than in `complete`: an
        // errored observable never completes, and a flag left set would leave
        // the panel spinning for as long as it is open.
        error: () => {
          this.labels = [];
          this.loadFailed = true;
          this.loading = false;
        },
      });
  }
}
